import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider, useLocation } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { Permission } from "@tcr/contracts";
import { isApiError } from "@/api/client";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { RealtimeProvider } from "@/realtime/RealtimeProvider";
import { Toaster } from "@/components/Toaster";
import { EmptyState, PageLoader } from "@/components/ui";
import { AppShell } from "@/components/AppShell";
import { LoginPage } from "@/features/auth/LoginPage";

const FleetPage = lazy(() => import("@/features/fleet/FleetPage"));
const RobotLayout = lazy(() => import("@/features/robot/RobotLayout"));
const RobotOverview = lazy(() => import("@/features/robot/RobotOverview"));
const MonitoringPage = lazy(() => import("@/features/monitoring/MonitoringPage"));
const CalibrationPage = lazy(() => import("@/features/calibration/CalibrationPage"));
const TeleopPage = lazy(() => import("@/features/teleop/TeleopPage"));
const ErrorsPage = lazy(() => import("@/features/errors/ErrorsPage"));
const RobotMissionsPage = lazy(() => import("@/features/missions/RobotMissionsPage"));
const DeploymentsPage = lazy(() => import("@/features/deployments/DeploymentsPage"));
const DiagnosticsPage = lazy(() => import("@/features/audit/DiagnosticsPage"));
const HistoryPage = lazy(() => import("@/features/robot/HistoryPage"));
const MissionsPage = lazy(() => import("@/features/missions/MissionsPage"));
const MissionDetailPage = lazy(() => import("@/features/missions/MissionDetailPage"));
const MapsPage = lazy(() => import("@/features/maps/MapsPage"));
const MapEditorPage = lazy(() => import("@/features/maps/MapEditorPage"));
const RegistryPage = lazy(() => import("@/features/admin/RegistryPage"));
const ErrorCatalogPage = lazy(() => import("@/features/admin/ErrorCatalogPage"));
const UsersPage = lazy(() => import("@/features/admin/UsersPage"));
const ApiTokensPage = lazy(() => import("@/features/admin/ApiTokensPage"));
const AuditPage = lazy(() => import("@/features/audit/AuditPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      // Don't hammer the API for errors a retry can't fix.
      retry: (count, err) => {
        if (isApiError(err) && err.status >= 400 && err.status < 500) return false;
        return count < 2;
      },
    },
  },
});

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") {
    return (
      <div className="flex h-full items-center justify-center">
        <PageLoader />
      </div>
    );
  }
  if (status === "anonymous") return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

/** Hides screens the user has no permission for (the server enforces it regardless). */
function Gate({ perm, children }: { perm: Permission; children: ReactNode }) {
  const { permissions } = useAuth();
  if (!permissions.has(perm)) {
    return (
      <EmptyState title="Not available">
        Your role doesn't include access to this screen (<code>{perm}</code>). Ask an administrator if you need it.
      </EmptyState>
    );
  }
  return <>{children}</>;
}

function S({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// A data router, so screens with unsaved edits can block in-app navigation (useBlocker).
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/fleet" replace />} />
        <Route path="fleet" element={<S><Gate perm="robot.view"><FleetPage /></Gate></S>} />
        <Route path="robots/:robotId" element={<S><Gate perm="robot.view"><RobotLayout /></Gate></S>}>
          <Route index element={<S><RobotOverview /></S>} />
          <Route path="monitoring" element={<S><Gate perm="telemetry.view"><MonitoringPage /></Gate></S>} />
          <Route path="calibration" element={<S><Gate perm="calibration.execute"><CalibrationPage /></Gate></S>} />
          <Route path="teleop" element={<S><Gate perm="teleop.drive"><TeleopPage /></Gate></S>} />
          <Route path="errors" element={<S><ErrorsPage /></S>} />
          <Route path="missions" element={<S><Gate perm="mission.view"><RobotMissionsPage /></Gate></S>} />
          <Route path="deployments" element={<S><DeploymentsPage /></S>} />
          <Route path="history" element={<S><HistoryPage /></S>} />
          <Route path="diagnostics" element={<S><Gate perm="diagnostics.view"><DiagnosticsPage /></Gate></S>} />
        </Route>
        <Route path="missions" element={<S><Gate perm="mission.view"><MissionsPage /></Gate></S>} />
        <Route path="missions/:missionId" element={<S><Gate perm="mission.view"><MissionDetailPage /></Gate></S>} />
        <Route path="maps" element={<S><Gate perm="map.view"><MapsPage /></Gate></S>} />
        <Route path="maps/:mapId" element={<S><Gate perm="map.view"><MapEditorPage /></Gate></S>} />
        <Route path="admin/registry" element={<S><Gate perm="registry.manage"><RegistryPage /></Gate></S>} />
        <Route path="admin/errors" element={<S><Gate perm="errors.manage"><ErrorCatalogPage /></Gate></S>} />
        <Route path="admin/users" element={<S><Gate perm="users.manage"><UsersPage /></Gate></S>} />
        <Route path="admin/tokens" element={<S><Gate perm="users.manage"><ApiTokensPage /></Gate></S>} />
        <Route path="audit" element={<S><Gate perm="audit.view"><AuditPage /></Gate></S>} />
        <Route path="*" element={<EmptyState title="Page not found" />} />
      </Route>
    </>,
  ),
);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RealtimeProvider>
          <RouterProvider router={router} />
          <Toaster />
        </RealtimeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
